import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useContext,
} from 'react';
import {
  Box,
  Button,
  IconButton,
  Divider,
  Chip,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WebAssetOffIcon from '@mui/icons-material/WebAssetOff';
import { DropResult } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

import { DigitalQuestion, QuestionBase, QuestionTypeV2 } from '../../redux/types';
import QuestionAdder from './QuestionAdder';
import StringQuestion from './string/StringQuestion';
import SingleChoiceQuestion from './singleChoice/SingleChoiceQuestion';
import MultiChoiceQuestion from './multiChoice/MultiChoiceQuestion';
import RatioQuestion from './ratio/RatioQuestion';
import NumberRangeQuestion from './numberRange/NumberRangeQuestion';
import SortableList from '../layout/SortableList';
import ChoiceWithTextQuestion from './choiceWithText/ChoiceWithTextQuestion';
import { getLastSortIndex } from '../../utilities/arrayHelper';
import SingleChoiceWithTextQuestion from './singleChoiceWithText/SingleChoiceWithTextQuestion';
import MultiChoiceWithTextQuestion from './multiChoiceWithText/MultiChoiceWithTextQuestion';
import QuestionCollapsed from '../survey/editor/QuestionCollapsed';
import SurveyOptionContext from '../../views/admin/SurveyOptionContext';

type QuestionV2Keys = keyof typeof QuestionTypeV2;
export type Props = {
  surveyId: number;
  trialId: number;
  sectionId?: number; // if sectionId is not provided question will be saved only under survey
  questions?: DigitalQuestion[];
  addQuestion: (question: Omit<DigitalQuestion, 'id'>) => void;
  updateQuestion: (question: DigitalQuestion) => void;
  deleteQuestion: (questionId: number) => void;
  onSortSave: (questions: DigitalQuestion[]) => Promise<void>;
  disabledQuestions?: QuestionV2Keys[];
  disableCopyQuestions?: boolean,
  isContextQuestion?: boolean;
  removeHideInAggregationCheckbox?: boolean;
  enableTNA?: boolean;
  showErrorNotification?: (val: string) => void;
  submissionExist?: boolean;
};

type SortableQuestionList = DigitalQuestion & { name: string, tag?: string }

const QuestionEditor: React.FC<Props> = ({
  surveyId,
  trialId,
  sectionId,
  questions,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  onSortSave,
  disabledQuestions,
  disableCopyQuestions,
  isContextQuestion = false,
  removeHideInAggregationCheckbox,
  enableTNA,
  showErrorNotification,
  submissionExist,
}: Props) => {
  const sortedListRef = useRef<SortableQuestionList[]>();
  const [dragMode, setDragMode] = useState<boolean>(false);
  const [editQuestion, setEditQuestion] = useState<number>();
  const [collapsedView, setCollapsedView] = useState<boolean>(true);
  const navigate = useNavigate();
  const disableHideAggregation = removeHideInAggregationCheckbox;
  const { disableEdit } = useContext(SurveyOptionContext);

  const editHandler = (questionId: number) => {
    setEditQuestion(questionId);
  };

  const cancelEditHandler = useCallback(() => {
    setEditQuestion(undefined);
  }, [setEditQuestion]);

  const onDragHandler = useCallback((result: DropResult, sortedList?: SortableQuestionList[]) => {
    if (sortedList) {
      sortedListRef.current = sortedList;
    }
  }, []);

  const sortableQuestionList = useMemo(() => questions?.map((q) => {
    const tagName = q.type as string;
    return ({ ...q, name: q.text, tag: tagName } as SortableQuestionList);
  }), [questions]);

  const updateQuestionHandler = (question: DigitalQuestion) => {
    if (submissionExist) {
      showErrorNotification?.('Cannot update question. Submissions exist for this survey.');
      return;
    }
    const { text, placeholder } = question;
    updateQuestion({
      ...question,
      text: text.trim(),
      placeholder: placeholder?.trim(),
    });
    setEditQuestion(undefined);
  };

  const saveSortOrderHandler = () => {
    const sortedList = sortedListRef.current;
    if (!sortedList) {
      return;
    }
    const updatedList = sortedList.map(({ name, tag, ...questionObj }, index) => {
      const updatedSortIndex: DigitalQuestion = { ...questionObj, sortIndex: index };
      return updatedSortIndex;
    });
    onSortSave(updatedList)
      .finally(() => {
        setDragMode(false);
        sortedListRef.current = undefined;
      });
  };

  const addQuestionHandler = (question: Omit<DigitalQuestion, 'id'>) => {
    if (submissionExist) {
      showErrorNotification?.('Cannot add question. Submissions exist for this survey.');
      return;
    }
    const sortIdx = getLastSortIndex<QuestionBase>(questions);
    const { text, placeholder } = question;
    const newQuestion = {
      ...question,
      sortIndex: sortIdx,
      isContextQuestion,
      text: text.trim(),
      placeholder: placeholder?.trim(),
    };
    addQuestion(newQuestion);
  };

  return (
    <Box>
      {
        sortableQuestionList && (
          <Toolbar
            disableGutters
            sx={{
              mb: 2,
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            {
              dragMode
                ? (
                  <>
                    <Typography sx={{ flexGrow: 1 }} variant="h6">Reorder Questions</Typography>
                    <Button variant="outlined" onClick={saveSortOrderHandler} size="small">Save</Button>
                    <Button variant="outlined" color="secondary" onClick={() => setDragMode(false)} size="small">Cancel</Button>
                  </>
                )
                : (
                  <>
                    <Typography sx={{ flexGrow: 1 }} variant="h6">Add, Update or Delete Questions</Typography>
                    {!disableCopyQuestions && (
                      <Tooltip title="Copy question from" placement="top">
                        <>
                          <IconButton
                            disabled={disableEdit}
                            onClick={() => {
                              const queryParam = isContextQuestion ? '?context=true' : '';
                              navigate(`/trial/${trialId}/survey/${surveyId}/section/${sectionId}/editor/copy${queryParam}`);
                            }}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </>
                      </Tooltip>
                    )}
                    <Tooltip title="Sort" placement="top">
                      <>
                        <IconButton
                          disabled={disableEdit}
                          onClick={() => { setDragMode(true); }}
                        >
                          <SortIcon />
                        </IconButton>
                      </>
                    </Tooltip>
                    {
                      collapsedView && (
                        <Tooltip title="Expand" placement="top">
                          <IconButton onClick={() => setCollapsedView(false)}>
                            <UnfoldMoreIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                    {
                      !collapsedView && (
                        <Tooltip title="Collapse" placement="top">
                          <IconButton onClick={() => setCollapsedView(true)}>
                            <UnfoldLessIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  </>
                )
            }
          </Toolbar>
        )
      }
      {
        !dragMode && (
          <Box aria-label="Question-editor">
            <Box>
              {
                !collapsedView && sortableQuestionList?.map((q) => (
                  <Paper elevation={0} sx={{ p: 3, my: 2 }} key={q.id}>
                    <Box>
                      {
                        q.type === QuestionTypeV2.String && (
                          editQuestion === q.id
                            ? <StringQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <StringQuestion mode="view" question={q} />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.SingleChoice && (
                          editQuestion === q.id
                            ? <SingleChoiceQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <SingleChoiceQuestion mode="view" question={q} />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.MultiChoice && (
                          editQuestion === q.id
                            ? <MultiChoiceQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <MultiChoiceQuestion mode="view" question={q} />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.Ratio && (
                          editQuestion === q.id
                            ? <RatioQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <RatioQuestion mode="view" question={q} />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.NumberRange && (
                          editQuestion === q.id
                            ? <NumberRangeQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <NumberRangeQuestion mode="view" question={q} />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.ChoiceWithText && (
                          editQuestion === q.id
                            ? <ChoiceWithTextQuestion mode="edit" question={q} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <ChoiceWithTextQuestion mode="view" question={q} />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.SingleChoiceWithText && (
                          editQuestion === q.id
                            ? <SingleChoiceWithTextQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} enableTNA={enableTNA} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <SingleChoiceWithTextQuestion mode="view" question={q} expand />
                        )
                      }
                      {
                        q.type === QuestionTypeV2.MultiChoiceWithText && (
                          editQuestion === q.id
                            ? <MultiChoiceWithTextQuestion mode="edit" question={q} disableHideAggregation={disableHideAggregation} enableTNA={enableTNA} surveyId={q.digitalSurveyId} onCancelEdit={cancelEditHandler} updateQuestion={updateQuestionHandler} />
                            : <MultiChoiceWithTextQuestion mode="view" question={q} expand />
                        )
                      }
                    </Box>
                    {
                      editQuestion === undefined && (
                        <Box display="flex" justifyContent="space-between" alignItems="flex-end" sx={{ my: 1 }}>
                          <Box display="flex" gap={2}>
                            <Chip label={q.type} size="small" />
                            {
                              q.hideAggregation && (
                                <Tooltip title={<FormattedMessage id="questionEditor.hideAggregationTooltip" />}>
                                  <WebAssetOffIcon fontSize="medium" sx={{ color: 'grey.500' }} />
                                </Tooltip>
                              )
                            }
                          </Box>
                          <Box gap={1} display="flex">
                            <Button disabled={disableEdit} size="small" variant="outlined" onClick={() => editHandler(q.id)}>Edit</Button>
                            <Button
                              disabled={disableEdit}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                if (submissionExist) {
                                  showErrorNotification?.('Cannot delete question. Submissions exist for this survey.');
                                  return;
                                }
                                deleteQuestion(q.id);
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      )
                    }
                    <Divider sx={{ mt: 3 }} />
                  </Paper>
                ))
              }
              {
                sortableQuestionList && collapsedView && (
                  <QuestionCollapsed
                    questions={sortableQuestionList}
                  />
                )
              }
            </Box>
            {
              editQuestion === undefined
              && !disableEdit && (
                <QuestionAdder
                  surveyId={surveyId}
                  sectionId={sectionId}
                  addQuestion={addQuestionHandler}
                  disabledQuestions={disabledQuestions}
                  disableHideAggregation={disableHideAggregation}
                  enableTNA={enableTNA}
                />
              )
            }
          </Box>
        )
      }
      {
        dragMode && sortableQuestionList && (
          <Box>
            <SortableList<SortableQuestionList>
              list={sortableQuestionList}
              droppableId="questionOrderOrganizer"
              onDragEndHandler={onDragHandler}
            />
          </Box>
        )
      }
    </Box>
  );
};

export default QuestionEditor;
